import React, { useState, useMemo, useEffect } from 'react';
import { filterRoomsByLessonType } from '../../../constants/roomTypes';
import {
  Box,
  Checkbox,
  TextField,
  Typography,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import QueryString from 'qs';
import moment from 'moment';

import CommonSelect from '../../../form-components/CommonSelect';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import { bulkUpdateLessons, bulkDeleteLessons, getAllLessonsByType } from '../../../api/lesson';
import { withOverlapConfirm } from '../../../utils/overlapConfirm';
import { errorMessages } from '../../../utils/methods';

import TicketSelectModal from './groupLessonTemplate/TicketSelectModal';
import { OPEN_SCHEDULE_TYPE, OPEN_SCHEDULE_TYPE_OPTIONS } from './groupLessonTemplate/AutoPublishCloseModal';
import { startOfWeekBy } from '../weekStart';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalFooter,
  TemplateModalOverlay,
} from './groupLessonTemplate/GroupLessonTemplateStyled';

// ============================================
// Constants
// ============================================
const DAY_LABELS = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
const PAGE_SIZE = 15;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i).padStart(2, '0'), label: String(i).padStart(2, '0'),
}));
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i * 5).padStart(2, '0'), label: String(i * 5).padStart(2, '0'),
}));

// 폐강 판단 시점(수업 시작 몇 시간 전): 1~23시간
const CLOSE_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => ({
  value: String(i + 1), label: `${i + 1}`,
}));

const YN_OPTIONS = [{ value: 'Y', label: '사용' }, { value: 'N', label: '사용 안 함' }];

const STATUS_CONFIG = {
  ended: { label: '종료', bg: '#F5F5F5', color: '#999' },
  deadline: { label: '예약마감', bg: '#E0F2F1', color: '#4AB3BC' },
  under: { label: '예약미달', bg: '#FFF3E0', color: '#E65100' },
  none: { label: '예약없음', bg: '#FBE9E7', color: '#D32F2F' },
  closed: { label: '비공개', bg: '#F5F5F5', color: '#999' },
};

const getStatus = item => {
  // 오늘 이전 수업은 종료, 오늘 수업은 종료시간 비교
  const now = moment();
  const today = now.format('YYYY-MM-DD');
  if (item.classDate < today) return 'ended';
  if (item.classDate === today && item.endTime) {
    const [endH, endM] = item.endTime.split(':').map(Number);
    if (now.hour() > endH || (now.hour() === endH && now.minute() >= endM)) return 'ended';
  }
  if (item.openYn === 'N') return 'closed';
  const total = (item.reservedMemberCount || 0) + (item.attendedMemberCount || 0);
  if (total === 0) return 'none';
  if (total >= (item.maxMemberCount || 0)) return 'deadline';
  return 'under';
};

// 뱃지 컴포넌트
const Badge = ({ bg, color, children }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: bg, color, fontSize: '12px', fontWeight: 600,
    borderRadius: '10px', padding: '2px 10px', minWidth: '40px',
  }}>
    {children}
  </Box>
);

// ============================================
// Component
// ============================================
const GroupLessonBulkEditModal = ({
  open,
  onClose,
  trainers = [],
  rooms = [],
  tickets = [],
  user,
  onRefresh,
  currentWeekStart,
}) => {
  const toast = useToast();

  // 기간 입력 (검색 전 임시값)
  // currentWeekStart 가 없을 때만 그룹 기본 주(월요일 시작) — 로케일 주에 기대지 않는다
  const weekStart = currentWeekStart || startOfWeekBy(moment(), 'MONDAY');
  const defaultFrom = weekStart.format('YYYY-MM-DD');
  const defaultTo = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
  const [inputDateFrom, setInputDateFrom] = useState(defaultFrom);
  const [inputDateTo, setInputDateTo] = useState(defaultTo);

  // 검색 버튼 클릭 시 실제 조회 기간
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // 자체 데이터 조회 (검색 버튼 클릭 시에만)
  const { data: lessonsData = [], isLoading: isDataLoading, refetch } = useQuery({
    queryKey: ['bulkEditLessons', user?.branch?.branchId, searchDateFrom, searchDateTo],
    queryFn: () => {
      const query = QueryString.stringify({
        courseClassType: 'GROUP_LESSON',
        headBranchId: user?.headBranch?.headBranchId,
        branchId: user?.branch?.branchId,
        classDateFrom: searchDateFrom,
        classDateTo: searchDateTo,
      });
      return getAllLessonsByType(query, user.token);
    },
    enabled: open && !!user?.branch?.branchId && !!user?.token && !!searchDateFrom && !!searchDateTo,
  });

  const handleSearch = () => {
    if (!inputDateFrom || !inputDateTo) {
      toast.error('기간을 설정해주세요');
      return;
    }
    setSearchDateFrom(inputDateFrom);
    setSearchDateTo(inputDateTo);
    setHasSearched(true);
    setSelectedIds([]);
    setPage(1);
    // 검색 종료일이 과거이면 종료 수업도 표시
    const today = moment().format('YYYY-MM-DD');
    if (inputDateTo < today) {
      setExcludeEnded(false);
    }
  };

  // 필터
  const [filterClassName, setFilterClassName] = useState('');
  const [filterTrainerId, setFilterTrainerId] = useState('');
  const [filterRoomId, setFilterRoomId] = useState('');
  const [filterDayOfWeek, setFilterDayOfWeek] = useState('');
  const [filterTimeFrom, setFilterTimeFrom] = useState('');
  const [filterTimeTo, setFilterTimeTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [excludeEnded, setExcludeEnded] = useState(true);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);

  // 선택
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);

  // 수정 단계
  const [editStep, setEditStep] = useState(null); // null | 'edit' | 'cancel'
  const [bulkValues, setBulkValues] = useState({
    className: '', trainerId: '', roomId: '', openYn: '',
    membersMaxCount: '', membersWaitMaxCount: '', courseTicketDeductionCount: '',
    startHour: '', startMinute: '', endHour: '', endMinute: '', classDate: '', ticketIds: [],
    openScheduledYn: '', openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openScheduledDate: '', openScheduledDay: '',
    openScheduledHour: '', openScheduledMinute: '',
    closeScheduledYn: '', closeScheduledTime: '', closeScheduledMinCount: '',
  });
  const [loading, setLoading] = useState(false);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);

  // 옵션
  const trainerOptions = trainers.map(t => ({ value: t.trainerId, label: t.trainerName }));
  const roomOptions = filterRoomsByLessonType(rooms, 'GROUP_LESSON').map(r => ({
    value: r.branchExerciseRoomId,
    label: r.branchExerciseRoomName,
  }));

  // 필터
  const filteredData = useMemo(() => {
    return lessonsData.filter(item => {
      if (filterClassName && !item.className?.toLowerCase().includes(filterClassName.toLowerCase())) return false;
      if (filterTrainerId && item.trainerId !== filterTrainerId) return false;
      if (filterRoomId && item.exerciseRoomId !== filterRoomId) return false;
      if (filterDayOfWeek !== '') {
        if (moment(item.classDate).day() !== Number(filterDayOfWeek)) return false;
      }
      if (filterTimeFrom && item.startTime < filterTimeFrom) return false;
      if (filterTimeTo && item.startTime > filterTimeTo) return false;
      if (filterStatus && getStatus(item) !== filterStatus) return false;
      if (excludeEnded && getStatus(item) === 'ended') return false;
      return true;
    }).sort((a, b) => {
      const d = (a.classDate || '').localeCompare(b.classDate || '');
      return d !== 0 ? d : (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [lessonsData, filterClassName, filterTrainerId, filterRoomId, filterDayOfWeek, filterTimeFrom, filterTimeTo, filterStatus, excludeEnded]);

  const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const allFilteredSelected = filteredData.length > 0 && filteredData.every(item => selectedIds.includes(item.courseClassTimeId));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds([]);
    } else {
      const allIds = filteredData.map(item => item.courseClassTimeId);
      setSelectedIds(allIds);
    }
  };

  const toggleOne = id => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const resetFilters = () => {
    setFilterClassName(''); setFilterTrainerId(''); setFilterRoomId('');
    setFilterDayOfWeek(''); setFilterTimeFrom(''); setFilterTimeTo('');
    setFilterStatus(''); setExcludeEnded(true);
    setSelectedIds([]); setPage(1);
  };

  const handleClose = () => {
    setSelectedIds([]);
    setEditStep(null);
    setSearchDateFrom('');
    setSearchDateTo('');
    setHasSearched(false);
    setInputDateFrom(defaultFrom);
    setInputDateTo(defaultTo);
    resetFilters();
    onClose();
  };

  // 일괄 수정
  const handleStartEdit = () => {
    if (selectedIds.length === 0) { toast.error('수정할 수업을 선택해주세요'); return; }
    setBulkValues({
      className: '', trainerId: '', roomId: '', openYn: '',
      membersMaxCount: '', membersWaitMaxCount: '', courseTicketDeductionCount: '',
      startHour: '', startMinute: '', endHour: '', endMinute: '', classDate: '', ticketIds: [],
      openScheduledYn: '', openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openScheduledDate: '', openScheduledDay: '',
    openScheduledHour: '', openScheduledMinute: '',
      closeScheduledYn: '', closeScheduledTime: '', closeScheduledMinCount: '',
    });
    setEditStep('edit');
  };

  const getChangeLabels = () => {
    const {
      trainerId, roomId, openYn, membersMaxCount, membersWaitMaxCount, courseTicketDeductionCount,
      className, startHour, startMinute, endHour, endMinute, classDate, ticketIds,
      openScheduledYn, openScheduleType, openScheduledDate, openScheduledDay, openScheduledHour, openScheduledMinute,
      closeScheduledYn, closeScheduledTime, closeScheduledMinCount,
    } = bulkValues;
    const labels = [];
    if (className) labels.push(`수업명 → ${className}`);
    if (trainerId) {
      const t = trainers?.find(tr => tr.trainerId === trainerId);
      labels.push(`강사 → ${t?.trainerName || trainerId}`);
    }
    if (roomId) {
      const r = rooms?.find(rm => rm.branchExerciseRoomId === roomId);
      labels.push(`강의실 → ${r?.branchExerciseRoomName || roomId}`);
    }
    if (openScheduledYn === 'Y') {
      labels.push('공개여부 → 비공개 (자동공개 설정 시각까지)');
    } else if (openYn) {
      labels.push(`공개여부 → ${openYn === 'Y' ? '공개' : '비공개'}`);
    }
    if (membersMaxCount) labels.push(`정원 → ${membersMaxCount}명`);
    if (membersWaitMaxCount) labels.push(`대기정원 → ${membersWaitMaxCount}명`);
    if (courseTicketDeductionCount) labels.push(`차감횟수 → ${courseTicketDeductionCount}회`);
    if (startHour && startMinute && endHour && endMinute) labels.push(`시간 → ${startHour}:${startMinute}~${endHour}:${endMinute}`);
    if (classDate) labels.push(`수업일 → ${classDate}`);
    if (ticketIds && ticketIds.length > 0) labels.push(`수강권 → ${ticketIds.length}개 선택`);
    if (openScheduledYn === 'Y') {
      labels.push(openScheduleType === OPEN_SCHEDULE_TYPE.FIXED
        ? `자동공개 → ${openScheduledDate} ${openScheduledHour}:${openScheduledMinute} 전체 공개`
        : `자동공개 → 수업 ${openScheduledDay}일 전 ${openScheduledHour}:${openScheduledMinute}`);
    }
    if (openScheduledYn === 'N') labels.push('자동공개 → 사용 안 함');
    if (closeScheduledYn === 'Y') labels.push(`자동폐강 → 수업 ${closeScheduledTime}시간 전, ${closeScheduledMinCount}명 이하`);
    if (closeScheduledYn === 'N') labels.push('자동폐강 → 사용 안 함');
    return labels;
  };

  const handleSubmitEditValidate = () => {
    const {
      trainerId, roomId, openYn, membersMaxCount, membersWaitMaxCount, courseTicketDeductionCount,
      className, startHour, startMinute, endHour, endMinute, classDate, ticketIds,
      openScheduledYn, openScheduleType, openScheduledDate, openScheduledDay, openScheduledHour, openScheduledMinute,
      closeScheduledYn, closeScheduledTime, closeScheduledMinCount,
    } = bulkValues;
    const hasTimeChange = startHour && startMinute && endHour && endMinute;
    const hasTicketChange = ticketIds && ticketIds.length > 0;
    const hasChange = trainerId || roomId || openYn || membersMaxCount || membersWaitMaxCount || courseTicketDeductionCount || className || hasTimeChange || hasTicketChange || classDate || openScheduledYn || closeScheduledYn;
    if (!hasChange) { toast.error('변경할 항목을 선택해주세요'); return; }
    if (hasTimeChange) {
      const startTotal = Number(startHour) * 60 + Number(startMinute);
      const endTotal = Number(endHour) * 60 + Number(endMinute);
      if (startTotal >= endTotal) { toast.error('종료시간이 시작시간보다 늦어야 합니다'); return; }
    }
    const openWhenMissing = openScheduleType === OPEN_SCHEDULE_TYPE.FIXED ? !openScheduledDate : openScheduledDay === '';
    if (openScheduledYn === 'Y' && (openWhenMissing || !openScheduledHour || !openScheduledMinute)) {
      toast.error(openScheduleType === OPEN_SCHEDULE_TYPE.FIXED
        ? '자동공개 설정 시 공개 날짜와 시각을 모두 입력해주세요'
        : '자동공개 설정 시 며칠 전, 공개 시각을 모두 입력해주세요');
      return;
    }
    if (closeScheduledYn === 'Y' && (!closeScheduledTime || closeScheduledMinCount === '')) {
      toast.error('자동폐강 설정 시 판단 시점, 최소 인원을 모두 입력해주세요');
      return;
    }
    // 정원/대기 검증: 선택 수업 중 현재 인원보다 적은 값 방지
    if (membersMaxCount) {
      const maxReserved = Math.max(...selectedIds.map(id => {
        const item = lessonsData?.find(d => (d.courseClassTimeId || d.courseClassId) === id);
        return item ? (item.reservedMemberCount || 0) + (item.attendedMemberCount || 0) : 0;
      }));
      if (Number(membersMaxCount) < maxReserved) {
        toast.error(`선택한 수업 중 예약 인원이 최대 ${maxReserved}명입니다. 그보다 적은 정원을 설정할 수 없습니다.`);
        return;
      }
    }
    if (membersWaitMaxCount) {
      const maxWaiting = Math.max(...selectedIds.map(id => {
        const item = lessonsData?.find(d => (d.courseClassTimeId || d.courseClassId) === id);
        return item ? (item.waitingMemberCount || 0) : 0;
      }));
      if (Number(membersWaitMaxCount) < maxWaiting) {
        toast.error(`선택한 수업 중 대기 인원이 최대 ${maxWaiting}명입니다. 그보다 적은 대기 정원을 설정할 수 없습니다.`);
        return;
      }
    }
    setEditConfirmOpen(true);
  };

  const handleSubmitEdit = async () => {
    setEditConfirmOpen(false);
    const {
      trainerId, roomId, openYn, membersMaxCount, membersWaitMaxCount, courseTicketDeductionCount,
      className, startHour, startMinute, endHour, endMinute, classDate, ticketIds,
      openScheduledYn, openScheduleType, openScheduledDate, openScheduledDay, openScheduledHour, openScheduledMinute,
      closeScheduledYn, closeScheduledTime, closeScheduledMinCount,
    } = bulkValues;
    const hasTimeChange = startHour && startMinute && endHour && endMinute;
    const hasTicketChange = ticketIds && ticketIds.length > 0;

    setLoading(true);
    try {
      const payload = {
        courseClassIds: selectedIds,
      };
      if (className) payload.className = className;
      if (trainerId) payload.trainerId = trainerId;
      if (roomId) payload.exerciseRoomId = roomId;
      if (openYn) payload.openYn = openYn;
      if (membersMaxCount) payload.membersMaxCount = Number(membersMaxCount);
      if (membersWaitMaxCount) payload.membersWaitMaxCount = Number(membersWaitMaxCount);
      if (courseTicketDeductionCount) payload.courseTicketDeductionCount = Number(courseTicketDeductionCount);
      if (hasTimeChange) {
        payload.startTime = `${startHour}:${startMinute}`;
        payload.endTime = `${endHour}:${endMinute}`;
      }
      if (classDate) payload.classDate = classDate;
      if (hasTicketChange) {
        // 수강권/일일권 분리 — 선택한 것만 남도록 양쪽 다 보냄
        const regularIds = ticketIds.filter(id =>
          tickets.find(t => t.courseTicketId === id && t.ticketType !== 'TRIAL_TICKET'));
        const trialIds = ticketIds.filter(id =>
          tickets.find(t => t.courseTicketId === id && t.ticketType === 'TRIAL_TICKET'));
        payload.ticketIds = regularIds;
        payload.trialTicketIds = trialIds;
      }
      if (openScheduledYn) {
        payload.openScheduledYn = openScheduledYn;
        if (openScheduledYn === 'Y') {
          payload.openScheduleType = openScheduleType;
          payload.openScheduledDay = openScheduleType === OPEN_SCHEDULE_TYPE.FIXED
            ? openScheduledDate : String(openScheduledDay);
          payload.openScheduledTime = `${openScheduledHour}:${openScheduledMinute}`;
          // 개별수정과 동일하게, 자동공개를 켜면 지정 시각 전까지는 비공개 상태여야 한다.
          payload.openYn = 'N';
        }
      }
      if (closeScheduledYn) {
        payload.closeScheduledYn = closeScheduledYn;
        if (closeScheduledYn === 'Y') {
          payload.closeScheduledTime = closeScheduledTime;
          payload.closeScheduledMinCount = Number(closeScheduledMinCount);
        }
      }

      await withOverlapConfirm(force => bulkUpdateLessons(payload, user.token, force));

      toast.success(`${selectedIds.length}개 수업이 수정되었습니다`);
      setEditStep(null);
      setSelectedIds([]);
      refetch();
      onRefresh?.('skip');
    } catch (error) {
      // 겹침 확인창에서 취소했으면 안내 없이 편집 상태를 유지한다
      if (!error?.silent) toast.error(errorMessages(error));
    }
    setLoading(false);
  };

  // 일괄 취소
  const handleStartCancel = () => {
    if (selectedIds.length === 0) { toast.error('취소할 수업을 선택해주세요'); return; }
    setEditStep('cancel');
  };

  const handleSubmitCancel = async () => {
    setLoading(true);
    try {
      await bulkDeleteLessons({ courseClassIds: selectedIds }, user.token
      );
      toast.success(`${selectedIds.length}개 수업이 취소되었습니다`);
      setEditStep(null);
      setSelectedIds([]);
      refetch();
      onRefresh?.('skip');
    } catch (error) {
      toast.error(errorMessages(error));
    }
    setLoading(false);
  };

  // 선택된 수업 목록 (수정/취소 모달에서 공용)
  const selectedItemsList = useMemo(() =>
    lessonsData.filter(item => selectedIds.includes(item.courseClassTimeId))
      .sort((a, b) => (a.classDate || '').localeCompare(b.classDate || '') || (a.startTime || '').localeCompare(b.startTime || '')),
    [lessonsData, selectedIds],
  );

  const SelectedLessonList = () => (
    <Box sx={{
      maxHeight: '180px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px',
      '&::-webkit-scrollbar': { width: '5px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
    }}>
      <Box component="table" sx={{
        width: '100%', borderCollapse: 'collapse',
        '& th': { fontSize: '11px', fontWeight: 600, color: '#888', padding: '6px 8px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 },
        '& td': { fontSize: '12px', color: '#333', padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', whiteSpace: 'nowrap' },
        '& tr:last-child td': { borderBottom: 'none' },
      }}>
        <thead><tr><th>날짜</th><th>요일</th><th>시간</th><th>수업명</th><th>강사</th><th>예약/정원</th></tr></thead>
        <tbody>
          {selectedItemsList.map(item => {
            const dow = moment(item.classDate).day();
            const currentCount = (item.reservedMemberCount || 0) + (item.attendedMemberCount || 0);
            return (
              <tr key={item.courseClassTimeId}>
                <td>{moment(item.classDate).format('MM.DD')}</td>
                <td style={{ fontWeight: 600, color: dow === 0 ? '#ef5350' : dow === 6 ? '#2196f3' : '#333' }}>{DAY_LABELS[dow]}</td>
                <td style={{ color: '#555' }}>{item.startTime}~{item.endTime}</td>
                <td style={{ textAlign: 'left', fontWeight: 500 }}>{item.className}</td>
                <td style={{ color: '#555' }}>{item.trainerName}</td>
                <td>{currentCount}/{item.maxMemberCount || '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );

  // 메인: 필터 + 테이블 + 선택
  return (
    <>
    <Modal open={open} onClose={handleClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <TemplateModalOverlay sx={{ width: '1100px', height: '85vh' }}>
        <ModalHeader>
          <ModalHeaderTitle>스케줄 일괄수정</ModalHeaderTitle>
          <CloseIcon onClick={handleClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* 검색 영역 */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: '10px',
            padding: '14px 20px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa',
          }}>
            {/* 안내문 */}
            <Typography sx={{ fontSize: '13px', color: '#888' }}>
              {hasSearched
                ? '수업을 선택한 후 하단의 스케줄 일괄수정 또는 일괄 취소 버튼을 눌러주세요.'
                : '변경할 수업이 포함된 기간을 선택한 후 검색 버튼을 눌러주세요.'}
            </Typography>

            {/* 1행: 기간 선택 + 검색 */}
            <Box sx={{
              display: 'flex', gap: '8px', alignItems: 'center',
              '& .MuiOutlinedInput-root': { height: '36px', fontSize: '14px', borderRadius: '6px' },
            }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555', minWidth: '32px' }}>기간</Typography>
              <CommonDatePicker
                value={inputDateFrom}
                onChange={date => setInputDateFrom(date ? moment(date).format('YYYY-MM-DD') : '')}
                placeholder="시작일"
                sx={{ width: '160px' }}
              />
              <Typography sx={{ fontSize: '14px', color: '#999' }}>~</Typography>
              <CommonDatePicker
                value={inputDateTo}
                onChange={date => setInputDateTo(date ? moment(date).format('YYYY-MM-DD') : '')}
                minDate={inputDateFrom}
                placeholder="종료일"
                sx={{ width: '160px' }}
              />
              <Box onClick={handleSearch}
                sx={{
                  fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
                  backgroundColor: '#4AB3BC', borderRadius: '6px', padding: '7px 16px',
                  cursor: 'pointer', '&:hover': { backgroundColor: '#3da0a8' },
                }}>
                검색
              </Box>
            </Box>

            {/* 필터 (검색 후에만 표시) */}
            {hasSearched && (
              <Box sx={{
                border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fff', padding: '12px 14px',
                display: 'flex', flexDirection: 'column', gap: '10px',
                '& .MuiOutlinedInput-root': { height: '34px', fontSize: '13px', borderRadius: '6px' },
                '& .MuiSelect-select': { fontSize: '13px' },
              }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>필터</Typography>
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" placeholder="수업명 검색" value={filterClassName}
                    onChange={e => { setFilterClassName(e.target.value); setPage(1); }}
                    sx={{
                      width: '160px',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
                      },
                    }} />
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: '#e0e0e0' }} />
                  <CommonSelect value={filterTrainerId} onChange={e => { setFilterTrainerId(e.target.value); setPage(1); }}
                    options={trainerOptions} emptyLabel="강사" width={120} />
                  <CommonSelect value={filterRoomId} onChange={e => { setFilterRoomId(e.target.value); setPage(1); }}
                    options={roomOptions} emptyLabel="강의실" width={120} />
                  <CommonSelect value={filterDayOfWeek} onChange={e => { setFilterDayOfWeek(e.target.value); setPage(1); }}
                    options={[
                      { value: '1', label: '월' }, { value: '2', label: '화' }, { value: '3', label: '수' },
                      { value: '4', label: '목' }, { value: '5', label: '금' }, { value: '6', label: '토' },
                      { value: '0', label: '일' },
                    ]} emptyLabel="요일" width={72} />
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: '#e0e0e0' }} />
                  <CommonSelect value={filterTimeFrom} onChange={e => { setFilterTimeFrom(e.target.value); setPage(1); }}
                    options={HOUR_OPTIONS.map(h => ({ value: `${h.value}:00`, label: `${h.label}시` }))}
                    emptyLabel="시작" width={80} />
                  <Typography sx={{ fontSize: '12px', color: '#bbb' }}>~</Typography>
                  <CommonSelect value={filterTimeTo} onChange={e => { setFilterTimeTo(e.target.value); setPage(1); }}
                    options={HOUR_OPTIONS.map(h => ({ value: `${h.value}:00`, label: `${h.label}시` }))}
                    emptyLabel="종료" width={80} />
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: '#e0e0e0' }} />
                  <CommonSelect value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                    options={[
                      { value: 'deadline', label: '예약마감' }, { value: 'under', label: '예약미달' },
                      { value: 'none', label: '예약없음' }, { value: 'closed', label: '비공개' },
                      { value: 'ended', label: '종료' },
                    ]} emptyLabel="상태" width={76} />
                  <Box sx={{ width: '1px', height: '20px', backgroundColor: '#e0e0e0' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setExcludeEnded(prev => !prev)}>
                    <Checkbox size="small" checked={excludeEnded}
                      sx={{ p: 0, mr: '3px', color: '#bbb', '&.Mui-checked': { color: '#4AB3BC' } }} />
                    <Typography sx={{ fontSize: '12px', color: '#555', whiteSpace: 'nowrap', userSelect: 'none' }}>
                      종료된 수업 제외
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '13px', color: '#555' }}>
                    검색결과 <b style={{ color: '#4AB3BC' }}>{filteredData.length}</b>건
                    {selectedIds.length > 0 && <span style={{ color: '#4AB3BC', fontWeight: 600 }}> · {selectedIds.length}건 선택</span>}
                  </Typography>
                  <Typography onClick={resetFilters}
                    sx={{ fontSize: '12px', color: '#aaa', cursor: 'pointer', '&:hover': { color: '#666' } }}>
                    필터 초기화
                  </Typography>
                </Box>
              </Box>
            )}

          </Box>

          {/* 테이블 */}
          <Box sx={{ flex: 1, overflow: 'auto', position: 'relative', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' } }}>
            <Backdrop open={isDataLoading} sx={{ position: 'absolute', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <CircularProgress sx={{ color: '#4AB3BC' }} />
            </Backdrop>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  {[
                    { label: <Checkbox size="small" checked={allFilteredSelected} indeterminate={selectedIds.length > 0 && !allFilteredSelected} onChange={toggleAll}
                        sx={{ p: 0, color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }} /> },
                    { label: '날짜' },
                    { label: '요일' },
                    { label: '시간' },
                    { label: '강의실' },
                    { label: '수업명' },
                    { label: '예약' },
                    { label: '강사' },
                    { label: '상태' },
                  ].map((col, i) => (
                    <th key={i} style={{
                      padding: '10px 10px', fontSize: '13px', fontWeight: 600, color: '#444',
                      borderBottom: '2px solid #4AB3BC', textAlign: 'center',
                      backgroundColor: '#f0f8f8', whiteSpace: 'nowrap',
                    }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedData.length === 0 ? (
                  <tr><td colSpan={9} style={{ height: 'calc(85vh - 320px)', verticalAlign: 'middle', color: '#aaa', textAlign: 'center', fontSize: '14px' }}>
                    {isDataLoading ? '' : hasSearched ? '검색 결과가 없습니다' : '기간을 선택하고 검색 버튼을 눌러주세요'}
                  </td></tr>
                ) : (
                  pagedData.map(item => {
                    const isSelected = selectedIds.includes(item.courseClassTimeId);
                    const status = getStatus(item);
                    const isEnded = status === 'ended';
                    const dayOfWeek = moment(item.classDate).day();
                    const currentCount = (item.reservedMemberCount || 0) + (item.attendedMemberCount || 0);
                    const td = {
                      padding: '8px 8px', fontSize: '13px', borderBottom: '1px solid #eee',
                      textAlign: 'center', whiteSpace: 'nowrap',
                      color: isEnded ? '#bbb' : '#333',
                    };

                    return (
                      <tr key={item.courseClassTimeId}
                        style={{
                          backgroundColor: isSelected ? '#EDF7F7' : isEnded ? '#fafafa' : 'transparent',
                          cursor: 'pointer', transition: 'background-color 0.1s',
                          opacity: isEnded ? 0.7 : 1,
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = isEnded ? '#f5f5f5' : '#fafafa'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = isEnded ? '#fafafa' : 'transparent'; }}
                        onClick={() => toggleOne(item.courseClassTimeId)}>
                        <td style={td}>
                          <Checkbox size="small" checked={isSelected} sx={{ p: 0, color: '#bbb', '&.Mui-checked': { color: '#4AB3BC' } }} />
                        </td>
                        <td style={{ ...td, fontWeight: 500 }}>{moment(item.classDate).format('MM.DD')}</td>
                        <td style={{ ...td, fontWeight: 700, color: isEnded ? '#bbb' : dayOfWeek === 0 ? '#ef5350' : dayOfWeek === 6 ? '#2196f3' : '#333' }}>
                          {DAY_LABELS[dayOfWeek]}
                        </td>
                        <td style={{ ...td, color: isEnded ? '#bbb' : '#555' }}>{item.startTime} ~ {item.endTime}</td>
                        <td style={{ ...td, color: isEnded ? '#ccc' : '#666' }}>{item.branchExerciseRoomName || '-'}</td>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: isEnded ? '#bbb' : '#111', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.className}
                        </td>
                        <td style={td}>{currentCount}/{item.maxMemberCount || '-'}</td>
                        <td style={{ ...td, color: isEnded ? '#bbb' : '#555' }}>{item.trainerName}</td>
                        <td style={td}>
                          <Badge bg={STATUS_CONFIG[status].bg} color={STATUS_CONFIG[status].color}>
                            {STATUS_CONFIG[status].label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Box>
          </Box>

          {/* 페이지네이션 */}
          <Box sx={{ borderTop: '1px solid #eee', p: '8px 0' }}>
            <CommonPagination totalElements={filteredData.length} limit={PAGE_SIZE} page={page}
              onChange={(_, value) => setPage(value)} />
          </Box>
        </Box>

        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={handleClose}>닫기</CommonModalButton>
          <CommonModalButton variant="cancel" onClick={handleStartCancel} disabled={selectedIds.length === 0}
            sx={{ '&:not(:disabled)': { color: '#D32F2F', borderColor: '#D32F2F' } }}>
            <DeleteOutlineIcon sx={{ fontSize: '15px', mr: '2px' }} />일괄 취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleStartEdit} disabled={selectedIds.length === 0}>
            스케줄 일괄수정 ({selectedIds.length})
          </CommonModalButton>
        </ModalFooter>
      </TemplateModalOverlay>
    </Modal>

    {/* 일괄 수정 모달 (메인 위에 쌓임) */}
    <Modal open={editStep === 'edit'} onClose={() => setEditStep(null)} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '560px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <ModalHeader>
          <ModalHeaderTitle>스케줄 일괄수정 ({selectedIds.length}건)</ModalHeaderTitle>
          <CloseIcon onClick={() => setEditStep(null)} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>
        <Box sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto' }}>
          {/* 선택된 수업 목록 */}
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '6px' }}>
              선택된 수업 ({selectedIds.length}건)
            </Typography>
            <SelectedLessonList />
          </Box>

          {/* 변경 항목 */}
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '6px' }}>
              변경 항목
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#888', mb: '10px' }}>
              변경할 항목만 입력하세요. <span style={{ color: '#D32F2F', fontWeight: 600 }}>* 미입력 항목은 기존 값이 유지됩니다.</span>
            </Typography>
            {/* 수업명 (전체 너비) */}
            <Box sx={{ mb: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>수업명</Typography>
              <TextField size="small" fullWidth placeholder="변경 안 함" value={bulkValues.className}
                onChange={e => setBulkValues(prev => ({ ...prev, className: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
            </Box>
            {/* 수강권 변경 */}
            <Box sx={{ mb: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>수강권 변경</Typography>
              <Box
                onClick={() => setTicketModalOpen(true)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  height: '36px', px: '12px', border: '1px solid #c4c4c4', borderRadius: '6px',
                  cursor: 'pointer', backgroundColor: (bulkValues.ticketIds || []).length > 0 ? '#f0f8f8' : '#fff',
                  '&:hover': { borderColor: '#4AB3BC' },
                }}>
                <Typography sx={{ fontSize: '13px', color: (bulkValues.ticketIds || []).length > 0 ? '#4AB3BC' : '#999' }}>
                  {(bulkValues.ticketIds || []).length > 0 ? `${(bulkValues.ticketIds || []).length}개 수강권 선택됨` : '변경 안 함 (클릭하여 선택)'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>강사</Typography>
                <CommonSelect value={bulkValues.trainerId}
                  onChange={e => setBulkValues(prev => ({ ...prev, trainerId: e.target.value }))}
                  options={trainerOptions} emptyLabel="변경 안 함" width="100%" />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>강의실</Typography>
                <CommonSelect value={bulkValues.roomId}
                  onChange={e => setBulkValues(prev => ({ ...prev, roomId: e.target.value }))}
                  options={roomOptions} emptyLabel="변경 안 함" width="100%" />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>정원</Typography>
                <TextField size="small" fullWidth placeholder="변경 안 함" value={bulkValues.membersMaxCount}
                  onChange={e => setBulkValues(prev => ({ ...prev, membersMaxCount: e.target.value.replace(/[^0-9]/g, '') }))}
                  InputProps={{ endAdornment: bulkValues.membersMaxCount ? <span style={{ color: '#999', fontSize: '12px' }}>명</span> : null }}
                  sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>대기 정원</Typography>
                <TextField size="small" fullWidth placeholder="변경 안 함" value={bulkValues.membersWaitMaxCount}
                  onChange={e => setBulkValues(prev => ({ ...prev, membersWaitMaxCount: e.target.value.replace(/[^0-9]/g, '') }))}
                  InputProps={{ endAdornment: bulkValues.membersWaitMaxCount ? <span style={{ color: '#999', fontSize: '12px' }}>명</span> : null }}
                  sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>차감 횟수</Typography>
                <TextField size="small" fullWidth placeholder="변경 안 함" value={bulkValues.courseTicketDeductionCount}
                  onChange={e => setBulkValues(prev => ({ ...prev, courseTicketDeductionCount: e.target.value.replace(/[^0-9]/g, '') }))}
                  InputProps={{ endAdornment: bulkValues.courseTicketDeductionCount ? <span style={{ color: '#999', fontSize: '12px' }}>회</span> : null }}
                  sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>공개 여부</Typography>
                <CommonSelect value={bulkValues.openYn}
                  onChange={e => setBulkValues(prev => ({ ...prev, openYn: e.target.value }))}
                  options={[{ value: 'Y', label: '공개' }, { value: 'N', label: '비공개' }]}
                  emptyLabel="변경 안 함" width="100%" />
              </Box>
            </Box>

            {/* 날짜 변경 */}
            <Box sx={{ mt: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>수업일 변경</Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={bulkValues.classDate || ''}
                onChange={e => setBulkValues(prev => ({ ...prev, classDate: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
              />
            </Box>

            {/* 시간 변경 */}
            <Box sx={{ mt: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>수업 시간 변경</Typography>
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <CommonSelect value={bulkValues.startHour}
                  onChange={e => setBulkValues(prev => ({ ...prev, startHour: e.target.value }))}
                  options={HOUR_OPTIONS} emptyLabel="시" width={70} />
                <Typography sx={{ fontSize: '12px', color: '#999' }}>:</Typography>
                <CommonSelect value={bulkValues.startMinute}
                  onChange={e => setBulkValues(prev => ({ ...prev, startMinute: e.target.value }))}
                  options={MINUTE_OPTIONS} emptyLabel="분" width={70} />
                <Typography sx={{ fontSize: '13px', color: '#999', mx: '4px' }}>~</Typography>
                <CommonSelect value={bulkValues.endHour}
                  onChange={e => setBulkValues(prev => ({ ...prev, endHour: e.target.value }))}
                  options={HOUR_OPTIONS} emptyLabel="시" width={70} />
                <Typography sx={{ fontSize: '12px', color: '#999' }}>:</Typography>
                <CommonSelect value={bulkValues.endMinute}
                  onChange={e => setBulkValues(prev => ({ ...prev, endMinute: e.target.value }))}
                  options={MINUTE_OPTIONS} emptyLabel="분" width={70} />
              </Box>
            </Box>

            {/* 자동공개 설정 */}
            <Box sx={{ mt: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>자동공개 설정</Typography>
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <CommonSelect value={bulkValues.openScheduledYn}
                  onChange={e => setBulkValues(prev => ({ ...prev, openScheduledYn: e.target.value }))}
                  options={YN_OPTIONS} emptyLabel="변경 안 함" width={110} />
                {bulkValues.openScheduledYn === 'Y' && (
                  <>
                    <CommonSelect value={bulkValues.openScheduleType}
                      onChange={e => setBulkValues(prev => ({ ...prev, openScheduleType: e.target.value }))}
                      options={OPEN_SCHEDULE_TYPE_OPTIONS} width={150} />
                    {bulkValues.openScheduleType === OPEN_SCHEDULE_TYPE.FIXED ? (
                      <CommonDatePicker
                        value={bulkValues.openScheduledDate}
                        onChange={date => setBulkValues(prev => ({ ...prev, openScheduledDate: date ? moment(date).format('YYYY-MM-DD') : '' }))}
                        placeholder="공개 날짜"
                        sx={{ width: '150px' }}
                      />
                    ) : (
                      <>
                        <Typography sx={{ fontSize: '12px', color: '#999' }}>수업</Typography>
                        <TextField size="small" placeholder="0" value={bulkValues.openScheduledDay}
                          onChange={e => setBulkValues(prev => ({ ...prev, openScheduledDay: e.target.value.replace(/[^0-9]/g, '') }))}
                          sx={{ width: '52px', '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
                        <Typography sx={{ fontSize: '12px', color: '#999' }}>일 전</Typography>
                      </>
                    )}
                    <CommonSelect value={bulkValues.openScheduledHour}
                      onChange={e => setBulkValues(prev => ({ ...prev, openScheduledHour: e.target.value }))}
                      options={HOUR_OPTIONS} emptyLabel="시" width={70} />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>:</Typography>
                    <CommonSelect value={bulkValues.openScheduledMinute}
                      onChange={e => setBulkValues(prev => ({ ...prev, openScheduledMinute: e.target.value }))}
                      options={MINUTE_OPTIONS} emptyLabel="분" width={70} />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>공개</Typography>
                  </>
                )}
              </Box>
              {bulkValues.openScheduledYn === 'Y' && (
                <Typography sx={{ fontSize: '11px', color: '#D32F2F', mt: '4px' }}>
                  설정 시 선택한 수업이 즉시 비공개로 전환되며, 지정한 시각에 자동으로 공개됩니다.
                </Typography>
              )}
            </Box>

            {/* 자동폐강 설정 */}
            <Box sx={{ mt: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: '4px' }}>자동폐강 설정</Typography>
              <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <CommonSelect value={bulkValues.closeScheduledYn}
                  onChange={e => setBulkValues(prev => ({ ...prev, closeScheduledYn: e.target.value }))}
                  options={YN_OPTIONS} emptyLabel="변경 안 함" width={110} />
                {bulkValues.closeScheduledYn === 'Y' && (
                  <>
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>수업 시작</Typography>
                    <CommonSelect value={bulkValues.closeScheduledTime}
                      onChange={e => setBulkValues(prev => ({ ...prev, closeScheduledTime: e.target.value }))}
                      options={CLOSE_HOUR_OPTIONS} emptyLabel="시간" width={70} />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>시간 전, 예약인원</Typography>
                    <TextField size="small" placeholder="0" value={bulkValues.closeScheduledMinCount}
                      onChange={e => setBulkValues(prev => ({ ...prev, closeScheduledMinCount: e.target.value.replace(/[^0-9]/g, '') }))}
                      sx={{ width: '52px', '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }} />
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>명 이하 폐강</Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={() => setEditStep(null)}>뒤로</CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSubmitEditValidate} disabled={loading}>
            {loading ? '수정 중...' : '수정하기'}
          </CommonModalButton>
        </ModalFooter>
      </Box>
    </Modal>

    {/* 일괄 취소 모달 (메인 위에 쌓임) */}
    <Modal open={editStep === 'cancel'} onClose={() => setEditStep(null)} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '500px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <ModalHeader>
          <ModalHeaderTitle>일괄 취소 확인</ModalHeaderTitle>
          <CloseIcon onClick={() => setEditStep(null)} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>
        <Box sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto' }}>
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#333', mb: '4px' }}>
              선택한 <b style={{ color: '#D32F2F' }}>{selectedIds.length}개</b> 수업을 취소하시겠습니까?
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#999', mb: '12px' }}>
              취소된 수업은 복구할 수 없으며, 예약된 회원이 있는 경우 취소가 불가합니다.
            </Typography>
          </Box>

          {/* 선택된 수업 목록 */}
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#333', mb: '6px' }}>
              취소 대상 ({selectedIds.length}건)
            </Typography>
            <SelectedLessonList />
          </Box>
        </Box>
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={() => setEditStep(null)}>뒤로</CommonModalButton>
          <CommonModalButton variant="delete" onClick={handleSubmitCancel} disabled={loading}>
            {loading ? '취소 중...' : '취소하기'}
          </CommonModalButton>
        </ModalFooter>
      </Box>
    </Modal>

    {/* 일괄수정 확인 */}
    <Dialog open={editConfirmOpen} onClose={() => setEditConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>일괄수정 확인</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '14px', color: '#333', mb: '12px' }}>
          선택한 <b style={{ color: '#0F7F85' }}>{selectedIds.length}개</b> 수업에 다음 변경사항을 적용합니다.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', p: '12px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee' }}>
          {getChangeLabels().map((label, i) => (
            <Typography key={i} sx={{ fontSize: '13px', color: '#333' }}>• {label}</Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '12px 24px' }}>
        <CommonModalButton variant="cancel" onClick={() => setEditConfirmOpen(false)}>취소</CommonModalButton>
        <CommonModalButton variant="submit" onClick={handleSubmitEdit} disabled={loading}>수정</CommonModalButton>
      </DialogActions>
    </Dialog>

    {/* 수강권 선택 모달 */}
    <TicketSelectModal
      open={ticketModalOpen}
      onClose={() => setTicketModalOpen(false)}
      tickets={tickets}
      selectedIds={bulkValues.ticketIds}
      onChangeIds={ids => setBulkValues(prev => ({ ...prev, ticketIds: ids }))}
    />
    </>
  );
};

export default GroupLessonBulkEditModal;
